/**
 * Sales Metrics Dashboard
 * Comprehensive view of sales pipeline performance, conversion rates, and forecasting
 */

import { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardBody,
  CardHeader,
  Grid,
  GridItem,
  Heading,
  HStack,
  Icon,
  Select,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatArrow,
  Text,
  VStack,
  Badge,
  Progress,
  Divider,
  useToast,
  Spinner,
  Flex,
} from '@chakra-ui/react';
import {
  MdTrendingUp,
  MdAttachMoney,
  MdPeople,
  MdCheckCircle,
  MdCancel,
  MdTimer,
  MdShowChart,
} from 'react-icons/md';
import { getLeadStats } from '../../../services/leadManagement';
import { getProposalStats } from '../../../services/proposalManagement';
import { LeadStats } from '../../../types/lead';
import { ProposalStats } from '../../../types/proposal';

interface SalesMetrics {
  leadStats: LeadStats;
  proposalStats: ProposalStats;
}

export function SalesMetrics() {
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [metrics, setMetrics] = useState<SalesMetrics | null>(null);
  const [timeRange, setTimeRange] = useState<'7d' | '30d' | '90d' | 'all'>('30d');

  // Load metrics
  const loadMetrics = async () => {
    try {
      setLoading(true);
      const [leadStats, proposalStats] = await Promise.all([
        getLeadStats(),
        getProposalStats(),
      ]);

      setMetrics({
        leadStats,
        proposalStats,
      });
    } catch (error) {
      toast({
        title: 'Error loading metrics',
        description: error instanceof Error ? error.message : 'Unknown error',
        status: 'error',
        duration: 5000,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMetrics();
  }, [timeRange]);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatPercent = (value: number) => {
    return `${value.toFixed(1)}%`;
  };

  if (loading || !metrics) {
    return (
      <Flex justify="center" align="center" h="400px">
        <Spinner size="xl" />
      </Flex>
    );
  }

  const { leadStats, proposalStats } = metrics;

  // Calculate additional metrics
  const activeLeads = leadStats.total - leadStats.byStatus.closed_won - leadStats.byStatus.closed_lost;
  const closedLeads = leadStats.byStatus.closed_won + leadStats.byStatus.closed_lost;
  const winRate = closedLeads > 0 ? (leadStats.byStatus.closed_won / closedLeads) * 100 : 0;
  const avgDealSize = leadStats.byStatus.closed_won > 0
    ? leadStats.totalPipelineValue / leadStats.total
    : leadStats.avgScore > 0 ? leadStats.avgDoorCount * 50 : 0;

  // Conversion funnel data
  const funnelStages = [
    {
      name: 'New Leads',
      count: leadStats.total,
      value: leadStats.totalPipelineValue,
      color: 'blue',
      percentage: 100,
    },
    {
      name: 'Qualified',
      count: leadStats.byStatus.qualifying + leadStats.byStatus.demo_scheduled + 
             leadStats.byStatus.demo_completed + leadStats.byStatus.proposal_sent + 
             leadStats.byStatus.negotiating + leadStats.byStatus.closed_won,
      value: leadStats.weightedPipelineValue,
      color: 'purple',
      percentage: leadStats.total > 0 
        ? ((leadStats.total - leadStats.byStatus.new) / leadStats.total) * 100 
        : 0,
    },
    {
      name: 'Demo Scheduled',
      count: leadStats.byStatus.demo_scheduled + leadStats.byStatus.demo_completed + 
             leadStats.byStatus.proposal_sent + leadStats.byStatus.negotiating + 
             leadStats.byStatus.closed_won,
      value: leadStats.weightedPipelineValue * 0.8,
      color: 'cyan',
      percentage: leadStats.conversionRates.leadToDemo,
    },
    {
      name: 'Proposal Sent',
      count: leadStats.byStatus.proposal_sent + leadStats.byStatus.negotiating + 
             leadStats.byStatus.closed_won,
      value: leadStats.weightedPipelineValue * 0.7,
      color: 'teal',
      percentage: leadStats.conversionRates.demoToProposal,
    },
    {
      name: 'Negotiating',
      count: leadStats.byStatus.negotiating + leadStats.byStatus.closed_won,
      value: proposalStats.totalValue,
      color: 'orange',
      percentage: leadStats.conversionRates.proposalToClose,
    },
    {
      name: 'Closed Won',
      count: leadStats.byStatus.closed_won,
      value: proposalStats.totalValue,
      color: 'green',
      percentage: leadStats.conversionRates.overallCloseRate,
    },
  ];

  // Source performance
  const sourcePerformance = Object.entries(leadStats.bySource).map(([source, count]) => ({
    source: source.replace('_', ' '),
    count,
    percentage: leadStats.total > 0 ? (count / leadStats.total) * 100 : 0,
  })).sort((a, b) => b.count - a.count);

  return (
    <Box pt={{ base: '130px', md: '80px', xl: '80px' }}>
      {/* Header */}
      <Flex justify="space-between" align="center" mb="6">
        <Heading size="lg">Sales Metrics</Heading>
        <Select maxW="200px" value={timeRange} onChange={(e) => setTimeRange(e.target.value as any)}>
          <option value="7d">Last 7 Days</option>
          <option value="30d">Last 30 Days</option>
          <option value="90d">Last 90 Days</option>
          <option value="all">All Time</option>
        </Select>
      </Flex>

      {/* Key Metrics */}
      <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} gap="6" mb="6">
        <Card>
          <CardBody>
            <Stat>
              <HStack>
                <Icon as={MdAttachMoney} boxSize="8" color="green.500" />
                <Box>
                  <StatLabel>Pipeline Value</StatLabel>
                  <StatNumber fontSize="2xl">
                    {formatCurrency(leadStats.totalPipelineValue)}
                  </StatNumber>
                  <StatHelpText>
                    Weighted: {formatCurrency(leadStats.weightedPipelineValue)}
                  </StatHelpText>
                </Box>
              </HStack>
            </Stat>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <Stat>
              <HStack>
                <Icon as={MdPeople} boxSize="8" color="blue.500" />
                <Box>
                  <StatLabel>Active Leads</StatLabel>
                  <StatNumber fontSize="2xl">{activeLeads}</StatNumber>
                  <StatHelpText>
                    {leadStats.total} total leads
                  </StatHelpText>
                </Box>
              </HStack>
            </Stat>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <Stat>
              <HStack>
                <Icon as={MdCheckCircle} boxSize="8" color="green.500" />
                <Box>
                  <StatLabel>Win Rate</StatLabel>
                  <StatNumber fontSize="2xl">{formatPercent(winRate)}</StatNumber>
                  <StatHelpText>
                    <StatArrow type={winRate > 20 ? 'increase' : 'decrease'} />
                    {leadStats.byStatus.closed_won} won / {closedLeads} closed
                  </StatHelpText>
                </Box>
              </HStack>
            </Stat>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <Stat>
              <HStack>
                <Icon as={MdTrendingUp} boxSize="8" color="purple.500" />
                <Box>
                  <StatLabel>Avg Deal Size</StatLabel>
                  <StatNumber fontSize="2xl">
                    {formatCurrency(avgDealSize)}
                  </StatNumber>
                  <StatHelpText>
                    {leadStats.avgDoorCount.toFixed(0)} avg doors
                  </StatHelpText>
                </Box>
              </HStack>
            </Stat>
          </CardBody>
        </Card>
      </SimpleGrid>

      {/* Conversion Funnel */}
      <Card mb="6">
        <CardHeader>
          <HStack>
            <Icon as={MdShowChart} />
            <Heading size="md">Conversion Funnel</Heading>
          </HStack>
        </CardHeader>
        <CardBody>
          <VStack spacing="4" align="stretch">
            {funnelStages.map((stage, index) => (
              <Box key={stage.name}>
                <HStack justify="space-between" mb="2">
                  <HStack spacing="4">
                    <Text fontWeight="bold" minW="150px">{stage.name}</Text>
                    <Badge colorScheme={stage.color}>{stage.count} leads</Badge>
                    <Text fontSize="sm" color="gray.600">
                      {formatCurrency(stage.value)}
                    </Text>
                  </HStack>
                  <HStack spacing="4">
                    <Text fontSize="sm" fontWeight="semibold" color={`${stage.color}.600`}>
                      {formatPercent(stage.percentage)}
                    </Text>
                  </HStack>
                </HStack>
                <Progress
                  value={stage.percentage}
                  colorScheme={stage.color}
                  size="lg"
                  borderRadius="md"
                />
                {index < funnelStages.length - 1 && (
                  <Text fontSize="xs" color="gray.500" mt="2" textAlign="center">
                    ↓ Drop-off: {formatPercent(funnelStages[index].percentage - stage.percentage)}
                  </Text>
                )}
              </Box>
            ))}
          </VStack>
        </CardBody>
      </Card>

      <Grid templateColumns="repeat(12, 1fr)" gap="6">
        {/* Pipeline by Status */}
        <GridItem colSpan={{ base: 12, lg: 6 }}>
          <Card>
            <CardHeader>
              <Heading size="md">Pipeline by Status</Heading>
            </CardHeader>
            <CardBody>
              <VStack spacing="3" align="stretch">
                {Object.entries(leadStats.byStatus)
                  .filter(([_, count]) => count > 0)
                  .sort(([, a], [, b]) => b - a)
                  .map(([status, count]) => {
                    const percentage = leadStats.total > 0 ? (count / leadStats.total) * 100 : 0;
                    return (
                      <HStack key={status} justify="space-between">
                        <HStack flex="1">
                          <Text textTransform="capitalize" minW="120px">
                            {status.replace('_', ' ')}
                          </Text>
                          <Progress
                            value={percentage}
                            flex="1"
                            colorScheme={
                              status === 'closed_won' ? 'green' :
                              status === 'closed_lost' ? 'red' :
                              status === 'proposal_sent' ? 'purple' :
                              'blue'
                            }
                            borderRadius="md"
                          />
                        </HStack>
                        <HStack spacing="4">
                          <Badge>{count}</Badge>
                          <Text fontSize="sm" color="gray.600" minW="50px" textAlign="right">
                            {formatPercent(percentage)}
                          </Text>
                        </HStack>
                      </HStack>
                    );
                  })}
              </VStack>
            </CardBody>
          </Card>
        </GridItem>

        {/* Lead Sources */}
        <GridItem colSpan={{ base: 12, lg: 6 }}>
          <Card>
            <CardHeader>
              <Heading size="md">Lead Sources</Heading>
            </CardHeader>
            <CardBody>
              <VStack spacing="3" align="stretch">
                {sourcePerformance.map((item) => (
                  <HStack key={item.source} justify="space-between">
                    <HStack flex="1">
                      <Text textTransform="capitalize" minW="120px">
                        {item.source}
                      </Text>
                      <Progress
                        value={item.percentage}
                        flex="1"
                        colorScheme="brand"
                        borderRadius="md"
                      />
                    </HStack>
                    <HStack spacing="4">
                      <Badge colorScheme="brand">{item.count}</Badge>
                      <Text fontSize="sm" color="gray.600" minW="50px" textAlign="right">
                        {formatPercent(item.percentage)}
                      </Text>
                    </HStack>
                  </HStack>
                ))}
              </VStack>
            </CardBody>
          </Card>
        </GridItem>

        {/* Proposal Metrics */}
        <GridItem colSpan={{ base: 12, lg: 6 }}>
          <Card>
            <CardHeader>
              <Heading size="md">Proposal Performance</Heading>
            </CardHeader>
            <CardBody>
              <VStack spacing="4" align="stretch">
                <Stat>
                  <StatLabel>Total Proposals</StatLabel>
                  <StatNumber>{proposalStats.total}</StatNumber>
                  <StatHelpText>
                    {proposalStats.byStatus.sent + proposalStats.byStatus.viewed} sent
                  </StatHelpText>
                </Stat>

                <Divider />

                <Stat>
                  <StatLabel>Conversion Rate</StatLabel>
                  <StatNumber fontSize="2xl" color="green.500">
                    {formatPercent(proposalStats.conversionRate)}
                  </StatNumber>
                  <StatHelpText>
                    <StatArrow type={proposalStats.conversionRate > 30 ? 'increase' : 'decrease'} />
                    {proposalStats.byStatus.accepted} accepted / {proposalStats.byStatus.sent + proposalStats.byStatus.viewed} sent
                  </StatHelpText>
                </Stat>

                <Divider />

                <Stat>
                  <StatLabel>Avg Proposal Value</StatLabel>
                  <StatNumber fontSize="xl">
                    {formatCurrency(proposalStats.avgProposalValue)}
                  </StatNumber>
                  <StatHelpText>
                    Total: {formatCurrency(proposalStats.totalValue)}
                  </StatHelpText>
                </Stat>

                <Divider />

                <Stat>
                  <StatLabel>Avg Time to Accept</StatLabel>
                  <StatNumber fontSize="xl">
                    {proposalStats.avgTimeToAccept.toFixed(1)} days
                  </StatNumber>
                  <StatHelpText>
                    {proposalStats.avgViewCount.toFixed(1)} avg views
                  </StatHelpText>
                </Stat>
              </VStack>
            </CardBody>
          </Card>
        </GridItem>

        {/* Win/Loss Analysis */}
        <GridItem colSpan={{ base: 12, lg: 6 }}>
          <Card>
            <CardHeader>
              <Heading size="md">Win/Loss Analysis</Heading>
            </CardHeader>
            <CardBody>
              <VStack spacing="4" align="stretch">
                <HStack justify="space-between" p="4" bg="green.50" borderRadius="md">
                  <HStack>
                    <Icon as={MdCheckCircle} color="green.500" boxSize="6" />
                    <Box>
                      <Text fontWeight="bold">Closed Won</Text>
                      <Text fontSize="sm" color="gray.600">
                        Success rate
                      </Text>
                    </Box>
                  </HStack>
                  <VStack align="end" spacing="0">
                    <Text fontSize="2xl" fontWeight="bold" color="green.600">
                      {leadStats.byStatus.closed_won}
                    </Text>
                    <Text fontSize="sm" color="gray.600">
                      {formatPercent(leadStats.conversionRates.overallCloseRate)}
                    </Text>
                  </VStack>
                </HStack>

                <HStack justify="space-between" p="4" bg="red.50" borderRadius="md">
                  <HStack>
                    <Icon as={MdCancel} color="red.500" boxSize="6" />
                    <Box>
                      <Text fontWeight="bold">Closed Lost</Text>
                      <Text fontSize="sm" color="gray.600">
                        Lost opportunities
                      </Text>
                    </Box>
                  </HStack>
                  <VStack align="end" spacing="0">
                    <Text fontSize="2xl" fontWeight="bold" color="red.600">
                      {leadStats.byStatus.closed_lost}
                    </Text>
                    <Text fontSize="sm" color="gray.600">
                      {closedLeads > 0 ? formatPercent((leadStats.byStatus.closed_lost / closedLeads) * 100) : '0%'}
                    </Text>
                  </VStack>
                </HStack>

                <Divider />

                <Box>
                  <Text fontWeight="bold" mb="2">Win Rate by Stage</Text>
                  <VStack spacing="2" align="stretch">
                    <HStack justify="space-between">
                      <Text fontSize="sm">Lead → Demo</Text>
                      <Badge colorScheme="blue">
                        {formatPercent(leadStats.conversionRates.leadToDemo)}
                      </Badge>
                    </HStack>
                    <HStack justify="space-between">
                      <Text fontSize="sm">Demo → Proposal</Text>
                      <Badge colorScheme="purple">
                        {formatPercent(leadStats.conversionRates.demoToProposal)}
                      </Badge>
                    </HStack>
                    <HStack justify="space-between">
                      <Text fontSize="sm">Proposal → Close</Text>
                      <Badge colorScheme="orange">
                        {formatPercent(leadStats.conversionRates.proposalToClose)}
                      </Badge>
                    </HStack>
                    <HStack justify="space-between">
                      <Text fontSize="sm" fontWeight="bold">Overall Close Rate</Text>
                      <Badge colorScheme="green" fontSize="md">
                        {formatPercent(leadStats.conversionRates.overallCloseRate)}
                      </Badge>
                    </HStack>
                  </VStack>
                </Box>
              </VStack>
            </CardBody>
          </Card>
        </GridItem>

        {/* Revenue Forecast */}
        <GridItem colSpan={{ base: 12 }}>
          <Card>
            <CardHeader>
              <HStack>
                <Icon as={MdTimer} />
                <Heading size="md">Revenue Forecast (Next 90 Days)</Heading>
              </HStack>
            </CardHeader>
            <CardBody>
              <SimpleGrid columns={{ base: 1, md: 4 }} gap="6">
                <Stat>
                  <StatLabel>Expected Revenue</StatLabel>
                  <StatNumber fontSize="xl" color="green.500">
                    {formatCurrency(leadStats.weightedPipelineValue)}
                  </StatNumber>
                  <StatHelpText>Weighted by probability</StatHelpText>
                </Stat>

                <Stat>
                  <StatLabel>Best Case</StatLabel>
                  <StatNumber fontSize="xl" color="blue.500">
                    {formatCurrency(leadStats.totalPipelineValue)}
                  </StatNumber>
                  <StatHelpText>If all deals close</StatHelpText>
                </Stat>

                <Stat>
                  <StatLabel>Most Likely</StatLabel>
                  <StatNumber fontSize="xl" color="purple.500">
                    {formatCurrency(leadStats.weightedPipelineValue * 0.7)}
                  </StatNumber>
                  <StatHelpText>Based on win rate</StatHelpText>
                </Stat>

                <Stat>
                  <StatLabel>Conservative</StatLabel>
                  <StatNumber fontSize="xl" color="orange.500">
                    {formatCurrency(leadStats.weightedPipelineValue * 0.5)}
                  </StatNumber>
                  <StatHelpText>Risk-adjusted</StatHelpText>
                </Stat>
              </SimpleGrid>

              <Divider my="4" />

              <Box>
                <Text fontWeight="bold" mb="3">Pipeline Health Indicators</Text>
                <SimpleGrid columns={{ base: 1, md: 3 }} gap="4">
                  <Box p="3" bg={activeLeads > 20 ? 'green.50' : 'orange.50'} borderRadius="md">
                    <Text fontSize="sm" color="gray.600">Active Leads</Text>
                    <Text fontSize="2xl" fontWeight="bold" color={activeLeads > 20 ? 'green.600' : 'orange.600'}>
                      {activeLeads}
                    </Text>
                    <Text fontSize="xs" color="gray.600">
                      Target: 20+ leads
                    </Text>
                  </Box>

                  <Box p="3" bg={leadStats.avgScore > 50 ? 'green.50' : 'orange.50'} borderRadius="md">
                    <Text fontSize="sm" color="gray.600">Avg Lead Score</Text>
                    <Text fontSize="2xl" fontWeight="bold" color={leadStats.avgScore > 50 ? 'green.600' : 'orange.600'}>
                      {leadStats.avgScore.toFixed(0)}/100
                    </Text>
                    <Text fontSize="xs" color="gray.600">
                      Target: 50+ score
                    </Text>
                  </Box>

                  <Box p="3" bg={proposalStats.conversionRate > 30 ? 'green.50' : 'orange.50'} borderRadius="md">
                    <Text fontSize="sm" color="gray.600">Proposal Win Rate</Text>
                    <Text fontSize="2xl" fontWeight="bold" color={proposalStats.conversionRate > 30 ? 'green.600' : 'orange.600'}>
                      {formatPercent(proposalStats.conversionRate)}
                    </Text>
                    <Text fontSize="xs" color="gray.600">
                      Target: 30%+ conversion
                    </Text>
                  </Box>
                </SimpleGrid>
              </Box>
            </CardBody>
          </Card>
        </GridItem>
      </Grid>
    </Box>
  );
}
